import { useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Link } from '@material-ui/core';
import classNames from 'classnames';

import lang from '../../../lib/language';
import { ExtraUserDetails } from '../../../lib/models/auth/types';
import { ENROLL_REQUEST_STATE } from '../../../lib/models/enroll';
import { ENROLL_AUTH_REQUEST_TYPE } from '../../../lib/models/enroll-requests';
import { RootState } from '../../../lib/store';
import { enrollAuth } from '../../../lib/store/auth';
import {
  PasswordValidationResult,
  PasswordValidationResultCleared,
} from '../../../lib/util/ValidationSchemes/PasswordSchema';
import {
  getEmailValidity,
  getFirstNameValidity,
  getLastNameValidity,
  validate,
  validateControl,
} from '../../../lib/util/ValidationSchemes/Validate';
import { ValidationType } from '../../../lib/util/ValidationSchemes/ValidationTypes';

import InputErrorLogo from '../../../assets/img/input-error-icon.svg';
import { InputControl, PasswordControl } from '../../../components';
import CheckboxControlV2 from '../../../components/CheckboxControl/CheckboxControlV2';
import {
  DefaultSignUpFormWithPoliciesErrors,
  SignUpFormWithPoliciesErrors,
} from '../../../components/Enroll/SignUp/SignUp.constants';
import config from '../../../config';
import { useIsMobile } from '../../../hooks';
import EnrollmentState from '../../../store-util/EnrollmentState';
import { UI_ROUTES } from '../../../util/routes';
import Layout from '../component/Layout';

import styles from './EnrollAuthInfo.module.scss';

type Props = ReduxProps;
const ADDITIONAL_HEIGHT_PER_ERROR = 24;

const EnrollAuthInfo = (props: Props): JSX.Element => {
  const {
    enrollRegister,
    isAppLoaded,
    requestState,
  } = props;

  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const { form } = EnrollmentState.use();
  const [formErrors, setFormErrors] = useState<SignUpFormWithPoliciesErrors>(DefaultSignUpFormWithPoliciesErrors);
  const [isSubmitButtonClicked, setIsSubmitButtonClicked] = useState<boolean>(false);
  const [
    passwordValidationResult,
    setPasswordValidationResult,
  ] = useState<PasswordValidationResult>(PasswordValidationResultCleared);
  const [additionalHeight, setAdditionalHeight] = useState(0);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!form.steps.isPersonalInfoPassed && isAppLoaded) {
      navigate(UI_ROUTES.firstEnroll, {
        replace: true,
      });
    }
  }, [form.steps.isPersonalInfoPassed, isAppLoaded]);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (requestState === ENROLL_REQUEST_STATE.READY) {
      EnrollmentState.setAuthInfoStepPassed(true);
      navigate(UI_ROUTES.emailConfirm);
    }
    if (requestState === ENROLL_REQUEST_STATE.ERROR) {
      setShowError(true);
    }
  }, [requestState]);// eslint-disable-line react-hooks/exhaustive-deps

  const validateForm = useCallback((): boolean => {
    const countryCheck = validateControl(
      form.country,
      [],
      lang.commonOnboardingInitialCountryLabel(),
      true,
    );

    const firstNameCheck = getFirstNameValidity(form.firstName);
    const lastNameCheck = getLastNameValidity(form.lastName);
    const emailCheck = getEmailValidity(form.email);

    setFormErrors({
      ...formErrors,
      email: emailCheck.errors,
      firstName: firstNameCheck.errors,
      lastName: lastNameCheck.errors,
      country: countryCheck.errors,
      privacyPolicyError: !form.isPrivacyPolicyApproved ? lang.commonPolicyAgreementErrorMessage() : '',
      tournamentPolicyError: !form.isTournamentPolicyApproved ? lang.commonTournamentPolicyAgreementErrorMessage() : '',
    });

    return (
      emailCheck.isValid && form.isPrivacyPolicyApproved && form.isTournamentPolicyApproved
    );
  }, [form.email, form.isPrivacyPolicyApproved, form.isTournamentPolicyApproved]);// eslint-disable-line react-hooks/exhaustive-deps

  const checkPasswordControl = useCallback((value: string): boolean => {
    const validationResult = validate(value, ValidationType.Password) as PasswordValidationResult;

    setPasswordValidationResult(validationResult);

    return (
      validationResult.specialChar
      && validationResult.maxCharacters
      && validationResult.number
      && validationResult.lowercaseLetter
      && validationResult.eightSymbols
      && validationResult.uppercaseLetter
      && validationResult.onlyLatinCharacters
    );
  }, []);

  const onSubmit = () => {
    setIsSubmitButtonClicked(true);
    setHasUserInteracted(true);
    if (validateForm()) {
      const extraDetails: ExtraUserDetails = {
        first_name: form.firstName,
        last_name: form.lastName,
        country_of_residence: form.country,
      };

      enrollRegister(form.email, form.password, form.password, extraDetails);
    }
  };

  useEffect(() => {
    if (hasUserInteracted) {
      const emailErrors = getEmailValidity(form.email).errors;

      // Calculate the total additional height for the policies
      const privacyPolicyError = !form.isPrivacyPolicyApproved ? 1 : 0;
      const tournamentPolicyError = !form.isTournamentPolicyApproved ? 1 : 0;

      // Calculate total error fields including email and policies
      const totalErrorFields = (emailErrors.length > 0 ? 1 : 0)
        + privacyPolicyError
        + tournamentPolicyError;

      // Set the additional height based on the number of errors
      setAdditionalHeight(totalErrorFields * ADDITIONAL_HEIGHT_PER_ERROR);
    }
  }, [form.email, form.isPrivacyPolicyApproved, form.isTournamentPolicyApproved, hasUserInteracted]);

  return (
    <Layout
      formTitle={lang.commonEnrollmentCreateProfileTitle()}
      onSubmit={onSubmit}
      displayBackButton
      displayNextButton
      nextButtonText={lang.commonOnboardingSignInButtonCreateAccount()}
      displayLoginButton={false}
      additionalHeight={additionalHeight}
    >
      <>
        {showError && (
          <div className={styles.error}>
            <div className={styles.errorContent}>
              <img className={styles.errorIcon} src={InputErrorLogo} alt="error" />
              <div>
                <div>
                  {lang.commonErrorRegister1()}
                  <a
                    href="mailto:support-pari@capital.bg"
                  >
                    <span className={styles.contact}>
                      {lang.commonErrorRegister2()}
                    </span>
                  </a>
                </div>
                {lang.commonErrorRegister3()}
              </div>
            </div>
          </div>
        )}
        <div className={classNames(styles.row, {
          [styles.errorRow]: isSubmitButtonClicked && getEmailValidity(form.email).errors.length > 0,
        })}
        >
          <span className={styles.title}>{lang.commonOnboardingSignInEmail()}</span>
          <InputControl
            name=""
            placeholder=""
            className={classNames(styles.input, styles.emailReg)}
            label={lang.commonEnrollmentEnterEmail()}
            value={form.email}
            onValueChange={(value) => {
              EnrollmentState.setEmail(String(value));
            }}
            errors={isSubmitButtonClicked ? getEmailValidity(form.email).errors : []}
            onSubmit={onSubmit}
          />
        </div>

        <div className={styles.row}>
          <span className={styles.titlePassword}>{lang.commonOnboardingSignInPassword()}</span>
          <PasswordControl
            name=""
            placeholder=""
            label={lang.commonEnrollmentEnterPassword()}
            visibleHint
            visibleAddon
            onSubmit={onSubmit}
            value={form.password}
            onValueChange={(value) => {
              EnrollmentState.setPassword(value);
              checkPasswordControl(value);
            }}
            validationFlags={passwordValidationResult}
            isSubmitButtonClicked={isSubmitButtonClicked}
          />
        </div>

        <div className={classNames(styles.row, {
          [styles.errorRow]: isSubmitButtonClicked && form.isPrivacyPolicyApproved,
        })}
        >
          <CheckboxControlV2
            id="privacyPolicyCheckbox"
            errorId="privacyPolicyCheckboxError"
            checked={form.isPrivacyPolicyApproved}
            centered={false}
            className={styles.checkbox}
            onChange={(value) => {
              EnrollmentState.setPrivacyPolicyApproved(value);
            }}
            component={(
              <p className={classNames(styles.text, { [styles.policyText]: !isMobile })}>
                {lang.commonEnrollmentApprovePrivacyPolicy1()}
                {' '}
                <Link className={classNames(styles.policyButton, styles.policyLink, { [styles.inlinePolicyButton]: !isMobile })} target="_blank" href={config.links.privacyPolicyCapitalUrl}>
                  {lang.commonEnrollmentApprovePrivacyPolicy2()}
                </Link>
              </p>
            )}
            error={!form.isPrivacyPolicyApproved ? formErrors.privacyPolicyError : undefined}
          />
        </div>

        <div className={classNames(styles.row, {
          [styles.errorRow]: isSubmitButtonClicked && form.isPrivacyPolicyApproved,
        })}
        >
          <CheckboxControlV2
            id="tournamentPolicyCheckbox"
            errorId="tournamentPolicyCheckboxError"
            checked={form.isTournamentPolicyApproved}
            centered={false}
            className={styles.checkbox}
            onChange={(value) => {
              EnrollmentState.setTournamentPolicyApproved(value);
            }}
            component={(
              <p className={classNames(styles.text, { [styles.policyText]: !isMobile })}>
                {lang.commonEnrollmentApprovePrivacyPolicy1()}
                {' '}
                <Link className={classNames(styles.policyButton, styles.policyLink, { [styles.inlinePolicyButton]: !isMobile })} target="_blank" href={config.links.tournamentRulesCapitalUrl}>
                  {lang.commonEnrollmentApproveTournamentRule()}
                </Link>
              </p>
            )}
            error={!form.isTournamentPolicyApproved
              ? formErrors.tournamentPolicyError : undefined}
          />
        </div>
      </>
    </Layout>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  enrollRegister: (
    email: string, password: string, confirmPassword: string, extraDetails: ExtraUserDetails,
  ) => dispatch(enrollAuth({
    requestType: ENROLL_AUTH_REQUEST_TYPE.REGISTER,
    email,
    password,
    confirmPassword,
    extraDetails,
  })),
});

const mapStateToProps = (state: RootState) => ({
  requestState: state.auth.enroll.status,
  status: state.auth.enroll.status,
  isAppLoaded: state.app.loaded,
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(EnrollAuthInfo);
